import { Component, computed, input, output } from '@angular/core';
import { WorkContact, WorkContactAndProjectsResultDto, WorkProject } from '@work-frontend-monorepo/work-rest-api';
import { TranslateModule } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { AppRoutePaths, AppRouteViewTypes } from '../../../app-route.enums';
import { NgTemplateOutlet } from '@angular/common';
import { GetFullnameOfContactPipe, GetProjectStatusColorVariantPipe, HighlightPipe } from '@work-frontend-monorepo/work-ui/pipes';
import { BadgeComponent } from '@work-frontend-monorepo/work-ui/layout';

export type MegasearchResultVariant = 'contact-focused' | 'project-focused';

export interface MegasearchResultClickedInterface {
  WorkContactAndProjectsResultDto: WorkContactAndProjectsResultDto;
  selectedProject?: WorkProject;
}

@Component({
  selector: 'work-office-global-megasearch-result-contact-and-projects',
  templateUrl: './global-megasearch-result-contact-and-projects.component.html',
  styleUrls: ['./global-megasearch-result-contact-and-projects.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    TranslateModule,
    HighlightPipe,
    NgTemplateOutlet,
    BadgeComponent,
    GetProjectStatusColorVariantPipe,
    GetFullnameOfContactPipe,
  ],
})
export class GlobalMegasearchResultContactAndProjectsComponent {
  // Signal inputs
  readonly searchterm = input.required<string>();
  readonly contactAndProjects = input.required<WorkContactAndProjectsResultDto>();
  readonly resultFocus = input<MegasearchResultVariant | undefined>(undefined);
  readonly renderContactAsLink = input<boolean>(false);
  readonly renderProjectAsLink = input<boolean>(false);
  readonly disableResultContactLinks = input<boolean>(false);
  readonly disableResultProjectLinks = input<boolean>(false);

  // Signal outputs
  readonly contactClicked = output<MegasearchResultClickedInterface>();
  readonly projectClicked = output<MegasearchResultClickedInterface>();

  // Computed signals
  protected readonly uniqueProjects = computed(() => {
    const projects = this.contactAndProjects().projects;
    if (!projects || projects.length === 0) {
      return [];
    }
    // Bei project-focused: Projekte unique machen (basierend auf ID)
    const uniqueMap = new Map<number, WorkProject>();
    projects.forEach((project) => {
      if (project.id && !uniqueMap.has(project.id)) {
        uniqueMap.set(project.id, project);
      }
    });
    return Array.from(uniqueMap.values());
  });
  protected readonly computedResultFocus = computed(() => {
    const resultFocus = this.resultFocus();
    if (resultFocus) {
      return resultFocus;
    }
    if (this.disableResultContactLinks()) {
      return 'project-focused';
    }
    return 'contact-focused';
  });

  // Protected constants
  protected readonly AppRoutePaths = AppRoutePaths;
  protected readonly AppRouteViewTypes = AppRouteViewTypes;

  // Public methods
  public getPropertyValueFromEntity(property: string, entity: WorkContact | WorkProject): string | undefined {
    if (['numberqualified', 'firstname', 'lastname', 'company', 'street', 'zipcode', 'city'].indexOf(property) > -1) {
      return undefined;
    }
    try {
      const objectKeys = Object.keys(entity);
      for (const key of objectKeys) {
        if (key && (key === property || key.toLowerCase() === property)) {
          let propertyValue = (entity as unknown as Record<string, unknown>)[key];
          if (typeof propertyValue === 'object' && propertyValue !== null) {
            propertyValue = (propertyValue as Record<string, unknown>)['name'] || undefined;
          }
          return propertyValue as string | undefined;
        }
      }
    } catch {
      return '-';
    }
    return undefined;
  }
}
